import React, { useEffect, useMemo, useState } from 'react';

export type FinancialAnalyticsTone = 'positive' | 'negative' | 'warning' | 'neutral';

export type FinancialAnalyticsRecord = {
  year: string;
  revenue?: number | null;
  operatingIncome?: number | null;
  netIncome?: number | null;
  assets?: number | null;
  liabilities?: number | null;
  equity?: number | null;
  operatingCashFlow?: number | null;
  capex?: number | null;
  freeCashFlow?: number | null;
  cashAndEquivalents?: number | null;
  totalBorrowings?: number | null;
  netDebt?: number | null;
};

export type FinancialAnalyticsMetric = {
  label: string;
  value: string;
  tone?: FinancialAnalyticsTone;
  helper?: string;
};

type StatementKey = 'income' | 'balance' | 'cashflow';

type FinancialAnalyticsPanelProps = {
  annual: FinancialAnalyticsRecord[];
  basis?: string;
  warning?: string;
  latestYear?: string;
  loading?: boolean;
  error?: string;
  metrics?: FinancialAnalyticsMetric[];
  qualityMetrics?: FinancialAnalyticsMetric[];
  compact?: boolean;
  full?: boolean;
  title?: string;
  subtitle?: string;
};

const COLORS = {
  text: '#191f28',
  sub: '#4e5968',
  muted: '#8b95a1',
  line: '#e5e8eb',
  soft: '#f7f8fa',
  surface: '#ffffff',
  revenue: '#74ddb3',
  operating: '#20b486',
  net: '#09896a',
  oldRevenue: '#d9dde4',
  oldOperating: '#c8cdd6',
  oldNet: '#b8bec9',
  positive: '#0b8f68',
  negative: '#e34b4b',
  warning: '#b7791f',
};

function finite(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatCompactWon(value: unknown): string {
  const n = finite(value);
  if (n === null) return '-';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_0000_0000_0000) return `${sign}${(abs / 1_0000_0000_0000).toFixed(abs >= 10_0000_0000_0000 ? 0 : 1)}조원`;
  if (abs >= 1_0000_0000) return `${sign}${(abs / 1_0000_0000).toFixed(abs >= 1000_0000_0000 ? 0 : 1)}억원`;
  if (abs >= 1_0000) return `${sign}${(abs / 1_0000).toFixed(abs >= 100_0000 ? 0 : 1)}만원`;
  return `${sign}${Math.round(abs).toLocaleString('ko-KR')}원`;
}

function toneColor(tone?: FinancialAnalyticsTone) {
  if (tone === 'positive') return COLORS.positive;
  if (tone === 'negative') return COLORS.negative;
  if (tone === 'warning') return COLORS.warning;
  return COLORS.text;
}

function FinancialBars({
  annual,
  selectedYear,
  onSelectYear,
  compact,
}: {
  annual: FinancialAnalyticsRecord[];
  selectedYear: string;
  onSelectYear: (year: string) => void;
  compact: boolean;
}) {
  const width = 760;
  const height = compact ? 210 : 250;
  const top = 18;
  const bottom = 42;
  const left = 8;
  const right = 8;
  const plotHeight = height - top - bottom;
  const plotWidth = width - left - right;

  const values = annual.flatMap((row) => [row.revenue, row.operatingIncome, row.netIncome])
    .map(finite)
    .filter((value): value is number => value !== null);

  if (!annual.length || !values.length) {
    return (
      <div style={{ height: compact ? 190 : 220, display: 'grid', placeItems: 'center', color: COLORS.muted, fontSize: 13 }}>
        차트로 표시할 연간 재무 데이터가 없습니다.
      </div>
    );
  }

  const rawMin = Math.min(0, ...values);
  const rawMax = Math.max(0, ...values);
  const span = rawMax - rawMin || 1;
  const y = (value: number) => top + ((rawMax - value) / span) * plotHeight;
  const zeroY = y(0);
  const groupWidth = plotWidth / annual.length;
  const barWidth = Math.max(8, Math.min(32, groupWidth * 0.19));
  const barGap = Math.max(3, Math.min(9, groupWidth * 0.045));
  const clusterWidth = barWidth * 3 + barGap * 2;

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="매출, 영업이익, 순이익 연간 추이"
        style={{ display: 'block', width: '100%', height: 'auto', minHeight: compact ? 180 : 220 }}
      >
        {[0.25, 0.5, 0.75].map((ratio) => {
          const gy = top + plotHeight * ratio;
          return <line key={ratio} x1={left} x2={width - right} y1={gy} y2={gy} stroke={COLORS.line} strokeWidth="1" />;
        })}
        <line x1={left} x2={width - right} y1={zeroY} y2={zeroY} stroke="#cdd3da" strokeWidth="1.2" />

        {annual.map((row, index) => {
          const center = left + groupWidth * index + groupWidth / 2;
          const startX = center - clusterWidth / 2;
          const selected = row.year === selectedYear;
          const barValues = [row.revenue, row.operatingIncome, row.netIncome];
          const activeColors = [COLORS.revenue, COLORS.operating, COLORS.net];
          const passiveColors = [COLORS.oldRevenue, COLORS.oldOperating, COLORS.oldNet];

          return (
            <g
              key={row.year}
              role="button"
              tabIndex={0}
              aria-label={`${row.year}년 재무 선택`}
              onClick={() => onSelectYear(row.year)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onSelectYear(row.year);
              }}
              style={{ cursor: 'pointer' }}
            >
              {barValues.map((rawValue, metricIndex) => {
                const value = finite(rawValue);
                if (value === null) return null;
                const valueY = y(value);
                const rectY = value >= 0 ? valueY : zeroY;
                const rectH = Math.max(1.5, Math.abs(zeroY - valueY));
                const x = startX + metricIndex * (barWidth + barGap);
                return (
                  <rect
                    key={`${row.year}-${metricIndex}`}
                    x={x}
                    y={rectY}
                    width={barWidth}
                    height={rectH}
                    rx={3}
                    fill={selected ? activeColors[metricIndex] : passiveColors[metricIndex]}
                    opacity={selected ? 1 : 0.92}
                  />
                );
              })}
              <text
                x={center}
                y={height - 15}
                textAnchor="middle"
                fontSize={compact ? 18 : 17}
                fontWeight={selected ? 800 : 600}
                fill={selected ? COLORS.text : COLORS.muted}
              >
                {row.year.length === 4 ? `${row.year.slice(2)}년` : row.year}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

const STATEMENT_META: Record<StatementKey, { label: string; rows: Array<{ label: string; key: keyof FinancialAnalyticsRecord }> }> = {
  income: {
    label: '손익계산서',
    rows: [
      { label: '매출', key: 'revenue' },
      { label: '영업이익', key: 'operatingIncome' },
      { label: '순이익', key: 'netIncome' },
    ],
  },
  balance: {
    label: '재무상태표',
    rows: [
      { label: '자산', key: 'assets' },
      { label: '부채', key: 'liabilities' },
      { label: '자본', key: 'equity' },
      { label: '현금 및 현금성자산', key: 'cashAndEquivalents' },
      { label: '총차입금', key: 'totalBorrowings' },
      { label: '순차입금', key: 'netDebt' },
    ],
  },
  cashflow: {
    label: '현금흐름표',
    rows: [
      { label: '영업현금흐름', key: 'operatingCashFlow' },
      { label: 'CAPEX', key: 'capex' },
      { label: 'FCF', key: 'freeCashFlow' },
    ],
  },
};

export default function FinancialAnalyticsPanel({
  annual,
  basis = '',
  warning = '',
  latestYear = '',
  loading = false,
  error = '',
  metrics = [],
  qualityMetrics = [],
  compact = false,
  full = true,
  title = '실적',
  subtitle = '매출과 이익의 흐름을 먼저 보고, 필요한 경우 재무제표 숫자까지 내려갑니다.',
}: FinancialAnalyticsPanelProps) {
  const normalizedAnnual = useMemo(
    () => annual
      .filter((row) => /^\d{4}$/.test(String(row.year ?? '')))
      .sort((a, b) => Number(a.year) - Number(b.year)),
    [annual],
  );

  const initialYear = latestYear && normalizedAnnual.some((row) => row.year === latestYear)
    ? latestYear
    : normalizedAnnual[normalizedAnnual.length - 1]?.year ?? '';
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [statementOpen, setStatementOpen] = useState(false);
  const [statement, setStatement] = useState<StatementKey>('income');

  useEffect(() => {
    if (!normalizedAnnual.length) {
      setSelectedYear('');
      return;
    }
    if (!normalizedAnnual.some((row) => row.year === selectedYear)) {
      setSelectedYear(initialYear || normalizedAnnual[normalizedAnnual.length - 1]?.year || '');
    }
  }, [initialYear, normalizedAnnual, selectedYear]);

  const selected = normalizedAnnual.find((row) => row.year === selectedYear) ?? normalizedAnnual[normalizedAnnual.length - 1];
  const statementMeta = STATEMENT_META[statement];

  return (
    <section
      className="pi-financial-analytics"
      style={{
        borderTop: `1px solid ${COLORS.line}`,
        background: COLORS.surface,
        padding: compact ? '18px 14px 8px' : '24px 20px 10px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: COLORS.text, fontSize: compact ? 18 : 21, fontWeight: 800, letterSpacing: '-.025em' }}>{title}</div>
          <div style={{ color: COLORS.muted, fontSize: 11.5, lineHeight: 1.55, marginTop: 4 }}>{subtitle}</div>
        </div>
        <span style={{ padding: '7px 11px', borderRadius: 10, background: COLORS.soft, color: COLORS.sub, fontSize: 11.5, fontWeight: 800 }}>연간</span>
      </div>

      {loading ? <div style={{ marginTop: 14, color: COLORS.muted, fontSize: 12 }}>연간 재무 원천을 불러오는 중입니다.</div> : null}
      {error ? <div style={{ marginTop: 14, color: COLORS.warning, fontSize: 12 }}>{error}</div> : null}

      <div style={{ marginTop: 12 }}>
        <FinancialBars annual={normalizedAnnual} selectedYear={selectedYear} onSelectYear={setSelectedYear} compact={compact} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
          gap: compact ? 8 : 12,
          marginTop: 4,
          padding: compact ? '0 2px 14px' : '0 8px 18px',
        }}
      >
        {[
          ['매출', selected?.revenue, COLORS.revenue],
          ['영업이익', selected?.operatingIncome, COLORS.operating],
          ['순이익', selected?.netIncome, COLORS.net],
        ].map(([label, value, color]) => (
          <div key={String(label)} style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: COLORS.sub, fontSize: compact ? 10.5 : 11.5, fontWeight: 700 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: String(color), flex: '0 0 auto' }} />
              <span>{label}</span>
            </div>
            <div style={{ marginTop: 4, color: COLORS.text, fontSize: compact ? 15 : 18, fontWeight: 800, letterSpacing: '-.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {formatCompactWon(value)}
            </div>
            <div style={{ marginTop: 1, color: COLORS.muted, fontSize: 9.8 }}>{selected?.year ? `${selected.year}년` : '-'}</div>
          </div>
        ))}
      </div>

      {metrics.length ? (
        <div style={{ borderTop: `1px solid ${COLORS.line}`, padding: compact ? '14px 0 4px' : '18px 0 6px' }}>
          <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 800, marginBottom: 10 }}>핵심 지표</div>
          <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(2,minmax(0,1fr))' : 'repeat(3,minmax(0,1fr))', gap: 1, background: COLORS.line, border: `1px solid ${COLORS.line}`, borderRadius: 12, overflow: 'hidden' }}>
            {metrics.map((metric) => (
              <div key={metric.label} style={{ minWidth: 0, padding: compact ? '10px 11px' : '12px 14px', background: '#fff' }}>
                <div style={{ color: COLORS.muted, fontSize: 10.5, fontWeight: 700 }}>{metric.label}</div>
                <div style={{ marginTop: 4, color: toneColor(metric.tone), fontSize: compact ? 13 : 14.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{metric.value}</div>
                {metric.helper ? <div style={{ marginTop: 3, color: COLORS.muted, fontSize: 9.5, lineHeight: 1.4 }}>{metric.helper}</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {full ? (
        <>
          <button
            type="button"
            onClick={() => setStatementOpen((value) => !value)}
            style={{
              width: '100%',
              minHeight: 54,
              marginTop: 12,
              border: 0,
              borderTop: `1px solid ${COLORS.line}`,
              background: '#fff',
              color: COLORS.text,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            재무제표 보기 <span aria-hidden="true" style={{ color: COLORS.muted, fontSize: 18 }}>{statementOpen ? '⌃' : '›'}</span>
          </button>

          {statementOpen ? (
            <div style={{ borderTop: `1px solid ${COLORS.line}`, paddingTop: 18 }}>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
                {(Object.keys(STATEMENT_META) as StatementKey[]).map((key) => {
                  const active = statement === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setStatement(key)}
                      style={{
                        flex: '0 0 auto',
                        border: 0,
                        borderRadius: 999,
                        background: active ? '#191f28' : COLORS.soft,
                        color: active ? '#fff' : COLORS.sub,
                        padding: '8px 13px',
                        fontSize: 11.5,
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {STATEMENT_META[key].label}
                    </button>
                  );
                })}
              </div>

              <div style={{ overflowX: 'auto', marginTop: 6, borderTop: `1px solid ${COLORS.line}`, borderBottom: `1px solid ${COLORS.line}` }}>
                <table style={{ width: '100%', minWidth: compact ? 640 : 760, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', left: 0, zIndex: 2, width: 150, padding: '12px 12px', background: '#fff', color: COLORS.muted, textAlign: 'left', fontSize: 10.5, fontWeight: 700, borderBottom: `1px solid ${COLORS.line}` }}>계정</th>
                      {normalizedAnnual.map((row) => (
                        <th key={row.year} style={{ padding: '12px 10px', background: row.year === selectedYear ? COLORS.soft : '#fff', color: row.year === selectedYear ? COLORS.text : COLORS.muted, textAlign: 'right', fontSize: 10.5, fontWeight: row.year === selectedYear ? 800 : 700, borderBottom: `1px solid ${COLORS.line}` }}>{row.year}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {statementMeta.rows.map((item) => (
                      <tr key={String(item.key)}>
                        <td style={{ position: 'sticky', left: 0, zIndex: 1, padding: '12px', background: '#fff', color: COLORS.sub, fontSize: 11.5, fontWeight: 700, borderBottom: `1px solid ${COLORS.line}` }}>{item.label}</td>
                        {normalizedAnnual.map((row) => (
                          <td key={`${row.year}-${String(item.key)}`} style={{ padding: '12px 10px', background: row.year === selectedYear ? '#fbfcfd' : '#fff', color: COLORS.text, textAlign: 'right', fontSize: 11.5, fontWeight: row.year === selectedYear ? 800 : 650, borderBottom: `1px solid ${COLORS.line}` }}>{formatCompactWon(row[item.key])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {qualityMetrics.length ? (
        <div style={{ marginTop: 18, borderTop: `1px solid ${COLORS.line}`, paddingTop: 18 }}>
          <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 800 }}>재무 품질</div>
          <div style={{ color: COLORS.muted, fontSize: 10.5, marginTop: 3 }}>확인된 연간 원천의 현금전환·투자부담·차입금 변화를 표시합니다.</div>
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: compact ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))', gap: 8 }}>
            {qualityMetrics.map((metric) => (
              <div key={metric.label} style={{ border: `1px solid ${COLORS.line}`, borderRadius: 10, padding: '10px 11px', minWidth: 0 }}>
                <div style={{ color: COLORS.muted, fontSize: 9.8, fontWeight: 700 }}>{metric.label}</div>
                <div style={{ marginTop: 4, color: toneColor(metric.tone), fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{metric.value}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(basis || warning) ? (
        <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: warning ? '#fff8e8' : COLORS.soft, color: warning ? COLORS.warning : COLORS.muted, fontSize: 10.5, lineHeight: 1.55 }}>
          {basis || '연간 재무 기준 확인'}{warning ? ` · ${warning}` : ''}
        </div>
      ) : null}
    </section>
  );
}
